import { inject, Service } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import { urlBuilder } from '../../api-helpers';
import {
  AssetDetail,
  AssetDownloadDetails,
  AssetDownloadRequestParams,
  AssetList,
  AssetListRequestQueryParams,
  BookmarkList,
  RequestAccessBody,
} from './types';
import { Observable, of } from 'rxjs';

@Service()
export class AssetService {
  private http = inject(HttpClient);

  getAssetDetail = (assetId: () => string | undefined) => {
    return httpResource<AssetDetail>(() => {
      if (!assetId()) return undefined;
      return urlBuilder(`/asset/${assetId()}`);
    });
  };

  getAssetCatalogList = (queryParams: () => AssetListRequestQueryParams) => {
    return httpResource<AssetList>(() => {
      return {
        url: urlBuilder('/asset/'),
        params: {
          ...queryParams(),
          get_filter_update: true,
        },
      };
    });
  };

  /**
   * Only returns assets when a list of IDs are provided.
   */
  getOnlySpecifiedAssetListById = (queryParams: () => AssetListRequestQueryParams) => {
    return httpResource<AssetList>(() => {
      if (queryParams()?.asset_ids?.length === 0) return undefined;

      return {
        url: urlBuilder('/asset/'),
        params: {
          ...queryParams(),
        },
      };
    });
  };

  public getDiscoverPageRecentBookmarks = httpResource<BookmarkList>(() => {
    return {
      url: urlBuilder('/bookmark/'),
      params: {
        limit: 4,
      },
    };
  });

  public listAllBookmarks = httpResource<BookmarkList>(() => urlBuilder('/bookmark/'));

  public addBookmark = (assetId: () => string) => {
    return this.http.post(urlBuilder(`/bookmark/${assetId()}`), null).subscribe({
      complete: () => {
        this.listAllBookmarks.reload();
        this.getDiscoverPageRecentBookmarks.reload();
      },
    });
  };
  public deleteBookmark = (assetId: () => string) => {
    return this.http.delete(urlBuilder(`/bookmark/${assetId()}`)).subscribe({
      complete: () => {
        this.listAllBookmarks.reload();
        this.getDiscoverPageRecentBookmarks.reload();
      },
    });
  };

  public getAssetDownloadDetails = (
    canDownload: () => boolean,
    assetId: () => string,
    params: AssetDownloadRequestParams,
  ): Observable<AssetDownloadDetails | null> => {
    // Immediate pre-flight check before making the network request
    if (!canDownload()) {
      return of(null);
    }

    const url = urlBuilder(`/asset/${assetId()}/download`);
    return this.http.post<AssetDownloadDetails>(url, params);
  };

  requestAccess = (assetId: () => string, body: RequestAccessBody) => {
    return this.http.post(urlBuilder(`/asset/${assetId()}/request-access`), body);
  };
}
